import React, { useRef, useEffect } from 'react';
import type { Dispatch } from 'redux';
import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import type { PreloadedQuery } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { withRouter } from '@atlassian/jira-router';
import { useFilters } from '@atlassian/jira-software-backlog-filters-store';
import type { Filters, FilterType } from '@atlassian/jira-software-filters';
import { MAX_AVATARS, CUSTOM_FILTER } from '../../../../model/filters/constants';
import { ISSUE_PARENT_PANEL, VERSION_PANEL } from '../../../../model/panels/constants';
import { isIssueParentPanelOpen, isVersionPanelOpen } from '../../../../services/panel';
import { cardAutoScrollRequestOnFilter } from '../../../../state/actions/cards/auto-scroll';
import {
	filteredCardsRequest,
	clearFilteredCards,
	clearFilteredCardsRequest,
} from '../../../../state/actions/cards/filtered-cards';
import { togglePanel } from '../../../../state/actions/panels';
import {
	getRapidViewId,
	getProjectName,
	getProjectKey,
	getIsCompanyManaged,
	projectIdSelector,
} from '../../../../state/selectors/app';
import {
	getOrderedAssignees,
	getOrderedLabels,
	getOrderedCardTypes,
	getCardIds,
	isFilteredCardsLoading,
} from '../../../../state/selectors/cards';
import { getCanInviteOthersToProject } from '../../../../state/selectors/config';
import { getFilters } from '../../../../state/selectors/filters';
import { getFirstLevelHierarchyName } from '../../../../state/selectors/issue-hierarchy';
import {
	issueParentsToListItemSelector,
	firstParentIssueTypeNameSelector,
} from '../../../../state/selectors/issue-parent';
import { isAnyModalOpen } from '../../../../state/selectors/modals';
import { getPanels } from '../../../../state/selectors/panels';
import { canEditBoardConfig } from '../../../../state/selectors/permissions';
import { isVersionsFeatureEnabled as versionsFeatureSelector } from '../../../../state/selectors/version';
import { useIsCMPBacklog } from '../../../../state/state-hooks/capabilities';
import type { State, Action } from '../../../../state/types';
import AddPeopleModal from './add-people-modal';
import FilterBar from './view';

export type OwnProps = {
	// eslint-disable-next-line jira/react/handler-naming
	syncFiltersWithRemoteStore: (arg1: Filters) => void;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
};

export const getIsCustomFilterManageLinkEnabled = (state: State) =>
	getIsCompanyManaged(state)
		? true // we always want to show the link so user can see read-only board settings page even with no edit board permission
		: canEditBoardConfig(state);

const isTMPBoardAddPeopleExperimentEnabled = (state: State) => {
	const canInviteOthersToProject = getCanInviteOthersToProject(state);
	return (
		canInviteOthersToProject &&
		expValEquals('open_invite_for_open_tmp_projects', 'cohort', 'experiment')
	);
};

export const mapStateToProps = (state: State) => {
	const isCompanyManaged = getIsCompanyManaged(state);
	return {
		assignees: getOrderedAssignees(state),
		filter: getFilters(state),
		panels: getPanels(state),
		labels: getOrderedLabels(state),
		issueTypes: getOrderedCardTypes(state),
		maxAvatars: MAX_AVATARS,
		isAddPeopleButtonEnabled: isCompanyManaged
			? canEditBoardConfig(state) // Existing permission check for CMP
			: // Permission and new experiment check for TMP
				canEditBoardConfig(state) || isTMPBoardAddPeopleExperimentEnabled(state),
		isCustomFilterManageLinkEnabled: getIsCustomFilterManageLinkEnabled(state),
		isVersionFilterSpotlightEnabled: canEditBoardConfig(state),
		issueParentsLabel: isCompanyManaged
			? getFirstLevelHierarchyName(state) || firstParentIssueTypeNameSelector(state)
			: firstParentIssueTypeNameSelector(state),
		issueParents: issueParentsToListItemSelector(state),
		isAnyModalOpen: isAnyModalOpen(state),
		boardId: Number(getRapidViewId(state)),
		isVersionsFeatureEnabled: versionsFeatureSelector(state),
		projectId: projectIdSelector(state),
		projectName: getProjectName(state),
		projectKey: getProjectKey(state),
		cardIds: getCardIds(state),
		isCustomFiltersLoading: isFilteredCardsLoading(state),
		isEpicPanelOpen: isIssueParentPanelOpen(),
		isVersionPanelOpen: isVersionPanelOpen(),
		isCompanyManaged,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
	dispatch,
});

const getOnChange = memoizeOne(
	(issueIds: number[], dispatch, syncFiltersWithRemoteStore: (arg1: Filters) => void) =>
		(
			filters: Filters,
			filterType: FilterType | null,
			_: AnalyticsAttributes,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			if (filterType === CUSTOM_FILTER) {
				// @ts-expect-error Argument of type 'UIAnalyticsEvent | undefined' is not assignable to parameter of type 'UIAnalyticsEvent'.
				dispatch(filteredCardsRequest(issueIds, analyticsEvent, filters[CUSTOM_FILTER]));
			} else {
				getLongTasksMetrics('filter').start('backlog');
			}
			syncFiltersWithRemoteStore(filters);

			if (filterType !== CUSTOM_FILTER) {
				dispatch(cardAutoScrollRequestOnFilter());
			}
		},
);

const getClearAllFilters = memoizeOne(
	(dispatch, isCompanyManaged) =>
		(event: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			isCompanyManaged
				? dispatch(clearFilteredCardsRequest(analyticsEvent))
				: dispatch(clearFilteredCards());

			dispatch(cardAutoScrollRequestOnFilter());
		},
);

const getOnToggleParentPanel = memoizeOne((dispatch: Dispatch<Action>) => () => {
	dispatch(togglePanel(ISSUE_PARENT_PANEL));
});

const getOnToggleVersionsPanel = memoizeOne((dispatch: Dispatch<Action>) => () => {
	dispatch(togglePanel(VERSION_PANEL));
});

export const mergeProps = (
	stateProps: ReturnType<typeof mapStateToProps>,
	dispatchProps: ReturnType<typeof mapDispatchToProps>,
	ownProps: OwnProps,
) => {
	const {
		assignees,
		filter,
		panels,
		labels,
		maxAvatars,
		isAddPeopleButtonEnabled,
		isCustomFilterManageLinkEnabled,
		isVersionFilterSpotlightEnabled,
		issueParentsLabel,
		issueParents,
		issueTypes,
		boardId,
		isVersionsFeatureEnabled,
		projectId,
		projectName,
		projectKey,
		cardIds,
		isCustomFiltersLoading,
		isVersionPanelOpen: isVersionsOpen,
		isEpicPanelOpen,
		isCompanyManaged,
	} = stateProps;
	const { dispatch } = dispatchProps;
	const { customFiltersQueryRef, versionFilterRelayQueryRef } = ownProps;

	return {
		assignees,
		filter,
		panels,
		labels,
		maxAvatars,
		isAddPeopleButtonEnabled,
		isCustomFilterManageLinkEnabled,
		isVersionFilterSpotlightEnabled,
		issueParentsLabel,
		issueParents,
		issueTypes,
		AddPeopleModal,
		boardId,
		isVersionsFeatureEnabled,
		isCustomFiltersLoading,
		projectId,
		projectName,
		projectKey,
		onChange: getOnChange(cardIds, dispatch, ownProps.syncFiltersWithRemoteStore),
		onToggleParentPanel: getOnToggleParentPanel(dispatch),
		isIssueParentPanelToggleChecked: isEpicPanelOpen,
		onToggleVersionsPanel: getOnToggleVersionsPanel(dispatch),
		isVersionPanelToggleChecked: isVersionsOpen,
		onClearAllButtonClicked: getClearAllFilters(dispatch, isCompanyManaged),
		customFiltersQueryRef,
		versionFilterRelayQueryRef,
		isCompanyManaged,
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

// @ts-expect-error - TS2345 - Argument of type 'ComponentType<WithIntlProps<APIProps & { intl: IntlShapeV2; customFiltersQueryRef?: PreloadedQuery<viewBacklogCustomFiltersQuery, Record<string, unknown>> | null | undefined; versionFilterRelayQueryRef?: PreloadedQuery<...> | ... 1 more ... | undefined; }>>' is not assignable to parameter of type 'Component<{ assignees: Person[]; filter: Filters; panels: PanelState; labels: string[]; maxAvatars: number; isAddPeopleButtonEnabled: boolean; isCustomFilterManageLinkEnabled: boolean; ... 16 more ...; versionFilterRelayQueryRef: PreloadedQuery<...> | ... 1 more ... | undefined; }>'.
const ConnectedFilterBar = flow(connector, withRouter)(FilterBar);

export type FilterBarProps = {
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({ customFiltersQueryRef, versionFilterRelayQueryRef }: FilterBarProps) => {
	const [filters, { setFilters }] = useFilters();
	const isCompanyManaged = useIsCMPBacklog();

	// Temporary remove custom filters from the url on initial load for team-managed backlog.
	// Need to be removed when applying custom filters on initial load.
	const self = useRef({ isMounted: false }).current;
	useEffect(() => {
		if (!self.isMounted) {
			if (isCompanyManaged) {
				setFilters(filters);
			} else {
				const { [CUSTOM_FILTER]: customFilters, ...otherFilters } = filters;
				setFilters(otherFilters);
			}
		}
		self.isMounted = true;
	}, [self, filters, setFilters, isCompanyManaged]);

	return (
		// @ts-expect-error - TS2739 - Type '{ syncFiltersWithRemoteStore: (filters: Filters) => void | Promise<void>; customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery, Record<...>> | null | undefined; versionFilterRelayQueryRef: PreloadedQuery<...> | ... 1 more ... | undefined; }' is missing the following properties from type 'RouteContext': location, query, route, match, action.
		<ConnectedFilterBar
			syncFiltersWithRemoteStore={setFilters}
			customFiltersQueryRef={customFiltersQueryRef}
			versionFilterRelayQueryRef={versionFilterRelayQueryRef}
		/>
	);
};
